import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Heart, Search } from 'lucide-react'
import { specialties } from '../data/specialties'
import { carePlans } from '../data/carePlans'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { useScrollBehavior } from '../hooks/useScrollBehavior'
import Footer from '../components/Footer'
import FloatingSearchBox from '../components/FloatingSearchBox'

const SpecialtyPage = () => {
  const { specialtyId } = useParams()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Scroll to top on page load
  useScrollToTop()

  // Scroll behavior for mobile
  const { isHeroVisible, showFloatingSearch } = useScrollBehavior()

  const specialty = specialties.find(s => s.id === specialtyId)
  const specialtyCarePlans = carePlans
    .filter(cp => cp.specialtyId === specialtyId)
    .sort((a, b) => a.title.localeCompare(b.title))

  if (!specialty) {
    return (
      <div className="App">
        <div className="container">
          <h1>Specialty not found</h1>
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    )
  }

  const handleCarePlanClick = (carePlanId) => {
    navigate(`/care-plan/${carePlanId}`)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      setShowSuggestions(false)
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`)
    }
  }

  const handleSearchInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)

    if (value.trim().length > 0) {
      // Generate suggestions from care plans and specialties
      const lowercaseQuery = value.toLowerCase()
      const suggestions = []

      // Get care plan suggestions
      carePlans.forEach(plan => {
        if (plan.title.toLowerCase().includes(lowercaseQuery) ||
            plan.description.toLowerCase().includes(lowercaseQuery) ||
            plan.category.toLowerCase().includes(lowercaseQuery)) {
          suggestions.push({
            id: plan.id,
            title: plan.title,
            type: 'care-plan',
            category: plan.category
          })
        }
      })

      // Get specialty suggestions
      specialties.forEach(specialty => {
        if (specialty.name.toLowerCase().includes(lowercaseQuery) ||
            specialty.description.toLowerCase().includes(lowercaseQuery)) {
          suggestions.push({
            id: specialty.id,
            title: specialty.name,
            type: 'specialty',
            category: 'Specialty'
          })
        }
      })

      // Limit to 5 suggestions and sort
      const limitedSuggestions = suggestions
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, 5)

      setSearchSuggestions(limitedSuggestions)
      setShowSuggestions(limitedSuggestions.length > 0)
    } else {
      setSearchSuggestions([])
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false)
    setSearchTerm('')

    if (suggestion.type === 'care-plan') {
      navigate(`/care-plan/${suggestion.id}`)
    } else {
      navigate(`/specialty/${suggestion.id}`)
    }
  }

  return (
    <div className="App">
      {/* Fixed Top Search Box - hide on mobile when hero is hidden */}
      <section className={`care-plan-search-fixed ${!isHeroVisible ? 'hidden-mobile' : ''}`}>
        <div className="container">
          <div className="search-header-wrapper">
            <button
              onClick={() => navigate(-1)}
              className="search-header-back-btn"
            >
              <ChevronLeft size={14} />
            </button>
            <div
              className="search-header-title clickable"
              onClick={() => navigate('/')}
            >
              Qmed Nursing Care Plans
            </div>
          </div>
          <form onSubmit={handleSearch} className="search-form-compact">
            <div className="search-input-container">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search other care plans..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                onFocus={() => searchTerm.trim() && setShowSuggestions(searchSuggestions.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="search-input-compact"
              />

              {/* Search Suggestions Dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="search-suggestions">
                  {searchSuggestions.map((suggestion) => (
                    <div
                      key={`${suggestion.type}-${suggestion.id}`}
                      className="search-suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="suggestion-title">{suggestion.title}</div>
                      <div className="suggestion-category">{suggestion.category}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      <section className={`care-plan-header-compact specialty-page hero-section ${!isHeroVisible ? 'hidden' : ''}`}>
        <div className="container">
          <div className="care-plan-header-content">
            <h1 className="care-plan-title-compact" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <specialty.icon size={40} />
              {specialty.name}
            </h1>
            <p className="care-plan-subtitle-compact">{specialty.description}</p>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="care-plan-content-compact">
          <h2 style={{ marginBottom: '1.5rem', color: '#1e293b', fontSize: '1.5rem', fontWeight: '600' }}>
            Care Plans ({specialtyCarePlans.length})
          </h2>

          {specialtyCarePlans.length > 0 ? (
            <div className="specialty-grid">
              {specialtyCarePlans.map((carePlan) => (
                <div
                  key={carePlan.id}
                  className="specialty-card"
                  onClick={() => handleCarePlanClick(carePlan.id)}
                >
                  <h3>
                    <Heart size={20} />
                    {carePlan.title}
                  </h3>
                  <p>{carePlan.description}</p>
                  <ChevronRight className="chevron" size={16} />
                </div>
              ))}
            </div>
          ) : (
            <div className="favorites-empty">
              <Heart size={48} />
              <h3>No care plans available</h3>
              <p>Care plans for this specialty are coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Search Box for Mobile */}
      <FloatingSearchBox isVisible={showFloatingSearch} />

      <Footer />
    </div>
  )
}

export default SpecialtyPage
